/**
 * Test Suite Configuration page component.
 *
 * Single-page form for generating `generator_config_v3.yaml` files.
 * Uses `useReducer` for complex form state and `useState` for transient
 * loading/error states. Renders Cloudscape layout with collapsible
 * config sections visible with sensible defaults. The "Generate Config"
 * button pre-populates sections with LLM-generated values, but users
 * can also edit defaults manually without the backend.
 *
 * Route: /testsuite/config
 */

import React, { useReducer, useState, useCallback } from 'react';
import {
  SpaceBetween,
  Container,
  Header,
  Alert,
} from '@amzn/awsui-components-react';

import {
  configPageReducer,
  initialConfigPageState,
} from './configPageReducer';
import {
  prePopulate,
  generateConfig,
  saveConfig,
} from './configGeneratorApi';
import { validateInputs, validateConfigSections } from './configGeneratorValidators';
import { serializeSchemaSummary } from './schemaConverter';
import {
  ConfigGeneratorApiError,
  SaveConfigResponse,
  ValidationErrors,
} from './types';

// Section components — each receives (state, dispatch, validationErrors) as props.
import ToolSelectionSection from './ToolSelectionSection';
import InputSection from './InputSection';
import ToolConfigSection from './ToolConfigSection';
import JargonPromptSection from './JargonPromptSection';
import TemplatesSection from './TemplatesSection';
import GroupExpansionsSection from './GroupExpansionsSection';
import SamplingSection from './SamplingSection';
import DatabaseConfigSection from './DatabaseConfigSection';
import OutputSection from './OutputSection';

// ─── Constants ─────────────────────────────────────────────────────────

const DEFAULT_SCHEMA_FILENAME = 'schema.yaml';
const DEFAULT_DOWNLOAD_FILENAME = 'generator_config_v3.yaml';
const YAML_MIME_TYPE = 'application/x-yaml';
const LLM_TIMEOUT_STATUS = 502;

// ─── Page Component ────────────────────────────────────────────────────

const TestSuiteConfigPage: React.FC = () => {
  const [state, dispatch] = useReducer(
    configPageReducer,
    initialConfigPageState,
  );

  // Transient loading states
  const [isPrePopulating, setIsPrePopulating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Transient error states
  const [prePopulateError, setPrePopulateError] =
    useState<ConfigGeneratorApiError | null>(null);
  const [generateError, setGenerateError] =
    useState<ConfigGeneratorApiError | null>(null);
  const [saveError, setSaveError] =
    useState<ConfigGeneratorApiError | null>(null);

  // Save result for success notification
  const [saveResult, setSaveResult] = useState<SaveConfigResponse | null>(null);

  // Validation errors for inline form field feedback
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {},
  );

  // ── Handler: Generate Config (pre-populate) ────────────────────────

  const handleGenerateConfig = useCallback(async () => {
    // Validate inputs first
    const inputErrors = validateInputs(state);
    if (Object.keys(inputErrors).length > 0) {
      setValidationErrors(inputErrors);
      return;
    }
    setValidationErrors({});

    // Build the schema filename from the tool name
    const schemaFilename = state.schemaSummary
      ? `${state.schemaSummary.tool_name}_schema.yaml`
      : DEFAULT_SCHEMA_FILENAME;

    const schemaContent = state.schemaSummary
      ? serializeSchemaSummary(state.schemaSummary)
      : state.schemaContent;

    setIsPrePopulating(true);
    setPrePopulateError(null);

    try {
      const response = await prePopulate({
        schema_content: schemaContent,
        schema_filename: schemaFilename,
        business_knowledge: state.businessKnowledge,
        root_questions: state.rootQuestions,
      });

      dispatch({ type: 'POPULATE_FROM_RESPONSE', response });
    } catch (error) {
      if (error instanceof ConfigGeneratorApiError) {
        setPrePopulateError(error);
      } else {
        setPrePopulateError(
          new ConfigGeneratorApiError(0, 'An unexpected error occurred'),
        );
      }
    } finally {
      setIsPrePopulating(false);
    }
  }, [
    state.selectedToolId,
    state.businessKnowledge,
    state.rootQuestions,
    state.schemaSummary,
    state.schemaContent,
  ]);

  // ── Handler: Preview YAML ──────────────────────────────────────────

  const handlePreviewYaml = useCallback(async () => {
    // Validate config sections before generation
    const sectionErrors = validateConfigSections(state);
    if (Object.keys(sectionErrors).length > 0) {
      setValidationErrors(sectionErrors);
      return;
    }
    setValidationErrors({});

    setIsGenerating(true);
    setGenerateError(null);

    try {
      const response = await generateConfig({
        llm: state.llm,
        tools: state.tools,
        root_questions: state.rootQuestionsConfig,
        database: state.databaseConfig,
        orderBy_selection: state.orderBySelection,
        stratified_sampling: state.stratifiedSampling,
        group_expansion_settings: {
          enabled: state.groupExpansionEnabled,
          probability: state.groupExpansionProbability,
        },
      });

      dispatch({
        type: 'SET_GENERATED_YAML',
        yaml: response.yaml_content,
        filename: response.filename,
      });
    } catch (error) {
      if (error instanceof ConfigGeneratorApiError) {
        setGenerateError(error);
      } else {
        setGenerateError(
          new ConfigGeneratorApiError(0, 'An unexpected error occurred'),
        );
      }
    } finally {
      setIsGenerating(false);
    }
  }, [
    state.llm,
    state.tools,
    state.rootQuestionsConfig,
    state.databaseConfig,
    state.dimensionsSelection,
    state.templates,
    state.groupExpansion,
    state.orderBySelection,
    state.stratifiedSampling,
    state.groupExpansionEnabled,
    state.groupExpansionProbability,
  ]);

  // ── Handler: Download YAML ─────────────────────────────────────────

  const handleDownload = useCallback(() => {
    if (!state.generatedYaml) return;

    const blob = new Blob([state.generatedYaml], { type: YAML_MIME_TYPE });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = state.suggestedFilename || DEFAULT_DOWNLOAD_FILENAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [state.generatedYaml, state.suggestedFilename]);

  // ── Handler: Save to S3 ────────────────────────────────────────────

  const handleSaveToS3 = useCallback(
    async (targetPath: string) => {
      if (!state.generatedYaml) return;

      setIsSaving(true);
      setSaveError(null);
      setSaveResult(null);

      try {
        const response = await saveConfig({
          yaml_content: state.generatedYaml,
          target_path: targetPath,
        });

        setSaveResult(response);
      } catch (error) {
        if (error instanceof ConfigGeneratorApiError) {
          setSaveError(error);
        } else {
          setSaveError(
            new ConfigGeneratorApiError(0, 'An unexpected error occurred'),
          );
        }
      } finally {
        setIsSaving(false);
      }
    },
    [state.generatedYaml],
  );

  // ── Derived values ─────────────────────────────────────────────────

  const toolName = state.schemaSummary?.tool_name ?? '';

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h1">Test Suite Configuration</Header>}>
        <SpaceBetween size="l">
          <ToolSelectionSection
            selectedToolId={state.selectedToolId}
            selectedRevisionTrack={state.selectedRevisionTrack}
            selectedRevision={state.selectedRevision}
            schemaSummary={state.schemaSummary}
            dispatch={dispatch}
          />

          <InputSection
            businessKnowledge={state.businessKnowledge}
            rootQuestions={state.rootQuestions}
            hasToolSelected={state.selectedToolId !== null}
            dispatch={dispatch}
            onGenerateConfig={handleGenerateConfig}
            isLoading={isPrePopulating}
            error={prePopulateError}
            validationErrors={validationErrors}
          />
        </SpaceBetween>
      </Container>

      {prePopulateError && (
        <Alert
          type="error"
          header="Configuration generation failed"
          dismissible
          onDismiss={() => setPrePopulateError(null)}
        >
          {prePopulateError.statusCode === LLM_TIMEOUT_STATUS
            ? 'The LLM service timed out. Please try again.'
            : prePopulateError.detail}
        </Alert>
      )}

      {/* Config sections are always visible with sensible defaults.
          "Generate Config" pre-populates them with LLM-generated values,
          but users can also edit the defaults manually. */}
      {state.isPrePopulated && (
        <Alert type="success">
          Configuration pre-populated from LLM. Review and edit the sections below.
        </Alert>
      )}

      <ToolConfigSection
        state={state}
        dispatch={dispatch}
        validationErrors={validationErrors}
      />

      <JargonPromptSection
        state={state}
        dispatch={dispatch}
        validationErrors={validationErrors}
      />

      <TemplatesSection
        state={state}
        dispatch={dispatch}
        validationErrors={validationErrors}
      />

      <GroupExpansionsSection
        state={state}
        dispatch={dispatch}
        validationErrors={validationErrors}
      />

      <SamplingSection
        state={state}
        dispatch={dispatch}
        validationErrors={validationErrors}
      />

      <DatabaseConfigSection
        state={state}
        dispatch={dispatch}
        validationErrors={validationErrors}
      />

      <OutputSection
        yamlContent={state.generatedYaml}
        filename={state.suggestedFilename}
        toolName={toolName}
        s3TargetPath={state.s3TargetPath}
        onPreviewYaml={handlePreviewYaml}
        onDownload={handleDownload}
        onSaveToS3={handleSaveToS3}
        isGenerating={isGenerating}
        isSaving={isSaving}
        saveResult={saveResult}
        saveError={saveError}
        generateError={generateError}
        dispatch={dispatch}
      />
    </SpaceBetween>
  );
};

export default TestSuiteConfigPage;
